import React from "react";
import Tooltip from "./Tooltip";
import Pagination from "./Pagination";
import Skeleton from "./Skeleton";
import { EmptyState, ErrorState } from "./State";

/**
 * Reusable Enterprise DataTable Component
 *
 * Provides a standardized table view across the entire application with:
 * - Premium dark header with typography and optional tooltips
 * - Hover & click states for interactive rows
 * - Integrated loading skeletons and empty/error states
 * - Built-in responsive scroll container
 * - Integrated Pagination with standard page size controls
 *
 * Props:
 * @param {Array} columns - Array of column definitions:
 *   [{ header, key, tip, tooltip, align, width, style, className, render: (val, row, idx) => node }]
 * @param {Array} data - Array of row data objects
 * @param {Function} onRowClick - (row, idx, event) => void
 * @param {string|Function} keyField - Unique key field name (default: "id") or key extractor function
 * @param {string|number} selectedId - ID of currently selected row (applies highlighted styling)
 * @param {boolean} loading - Displays loading skeleton rows
 * @param {number} loadingRows - Number of skeleton rows to render when loading (default: 5)
 * @param {string} error - Error message to display
 * @param {Function} onRetry - Retry callback for error state
 * @param {string} emptyTitle - Title for EmptyState
 * @param {string} emptySubtitle - Subtitle for EmptyState
 * @param {string} emptyIcon - Icon name for EmptyState (default: "search")
 * @param {ReactNode} emptyState - Custom EmptyState component override
 * @param {number} page - Current active page number (1-based)
 * @param {number} total - Total records count
 * @param {number} pageSize - Number of items per page
 * @param {Function} onPageChange - Callback when page changes: (newPage) => void
 * @param {string} itemLabel - Label for pagination count (e.g. "servers", "tickets")
 * @param {string} containerClassName - Custom class for table container
 * @param {object} containerStyle - Custom inline styles for container
 * @param {string} tableClassName - Custom class for table element
 * @param {object} tableStyle - Custom inline styles for table element
 * @param {Function} rowStyle - (row, idx) => style object
 * @param {Function} rowClassName - (row, idx) => className string
 * @param {ReactNode} children - Optional children for custom manual thead/tbody markup
 */
export default function DataTable({
  columns = [],
  data = [],
  onRowClick,
  keyField = "id",
  selectedId,
  loading = false,
  loadingRows = 5,
  error = null,
  onRetry,
  emptyTitle = "No records found",
  emptySubtitle = "No matching records found for current filters.",
  emptyIcon = "search",
  emptyState = null,
  page,
  total,
  pageSize,
  onPageChange,
  itemLabel = "items",
  containerClassName = "",
  containerStyle = {},
  tableClassName = "",
  tableStyle = {},
  rowStyle,
  rowClassName,
  children,
}) {
  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (loading) {
    return (
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {Array.from({ length: loadingRows }).map((_, i) => (
          <Skeleton key={i} height={44} style={{ borderRadius: 7 }} />
        ))}
      </div>
    );
  }

  // If custom children are provided, render children inside container with pagination
  if (children) {
    return (
      <>
        <div
          className={`premium-table-container ${containerClassName}`}
          style={containerStyle}
        >
          <table
            className={`premium-table ${tableClassName}`}
            style={tableStyle}
          >
            {children}
          </table>
        </div>
        {page && onPageChange && total > 0 && pageSize && (
          <Pagination
            page={page}
            total={total}
            pageSize={pageSize}
            onChange={onPageChange}
            itemLabel={itemLabel}
          />
        )}
      </>
    );
  }

  if (!data || data.length === 0) {
    if (emptyState) return emptyState;
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        subtitle={emptySubtitle}
      />
    );
  }

  const getKey = (row, idx) => {
    if (typeof keyField === "function") return keyField(row, idx);
    return row?.[keyField] ?? idx;
  };

  const isSelected = (row, idx) => {
    if (selectedId === undefined || selectedId === null) return false;
    const key = getKey(row, idx);
    return key === selectedId || row?.id === selectedId;
  };

  // If pagination props provided and data is not sliced by parent (i.e. data.length > pageSize and total equals data.length)
  const isClientSidePaging =
    page && pageSize && total && total === data.length && data.length > pageSize;
  const displayData = isClientSidePaging
    ? data.slice((page - 1) * pageSize, page * pageSize)
    : data;
  const totalCount = total !== undefined ? total : data.length;

  return (
    <>
      <div
        className={`premium-table-container ${containerClassName}`}
        style={containerStyle}
      >
        <table
          className={`premium-table ${tableClassName}`}
          style={{ width: "100%", ...tableStyle }}
        >
          <thead>
            <tr>
              {columns.map((col, idx) => {
                const headerText = col.header || col.label || "";
                const tooltipText = col.tip || col.tooltip;
                const headerAlign = col.align || "left";

                return (
                  <th
                    key={col.key || idx}
                    style={{
                      textAlign: headerAlign,
                      width: col.width,
                      ...col.headerStyle,
                      ...col.style,
                    }}
                    className={col.headerClassName || col.className || ""}
                  >
                    {tooltipText ? (
                      <Tooltip text={tooltipText}>
                        <span style={{ cursor: "default" }}>{headerText}</span>
                      </Tooltip>
                    ) : (
                      <span>{headerText}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, rowIdx) => {
              const rowKey = getKey(row, rowIdx);
              const active = isSelected(row, rowIdx);
              const customStyle =
                typeof rowStyle === "function"
                  ? rowStyle(row, rowIdx)
                  : rowStyle || {};
              const customClass =
                typeof rowClassName === "function"
                  ? rowClassName(row, rowIdx)
                  : rowClassName || "";

              return (
                <tr
                  key={rowKey}
                  onClick={onRowClick ? (e) => onRowClick(row, rowIdx, e) : undefined}
                  className={customClass}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    background: active ? "#EFF6FF" : undefined,
                    ...customStyle,
                  }}
                >
                  {columns.map((col, colIdx) => {
                    const cellAlign = col.align || "left";
                    const cellVal = col.key ? row?.[col.key] : undefined;

                    return (
                      <td
                        key={col.key || colIdx}
                        style={{
                          textAlign: cellAlign,
                          ...col.cellStyle,
                          ...col.style,
                        }}
                        className={col.cellClassName || col.className || ""}
                      >
                        {col.render
                          ? col.render(cellVal, row, rowIdx)
                          : cellVal !== undefined && cellVal !== null
                          ? String(cellVal)
                          : "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {page && onPageChange && totalCount > 0 && pageSize && (
        <Pagination
          page={page}
          total={totalCount}
          pageSize={pageSize}
          onChange={onPageChange}
          itemLabel={itemLabel}
        />
      )}
    </>
  );
}
